import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import pb from '../services/pocketbase';

const FavoritesContext = createContext();

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}

export function FavoritesProvider({ children }) {
  const [favorites, setFavorites] = useState([]);
  const [guestFavorites, setGuestFavorites] = useState([]);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Charger les favoris invité depuis localStorage au démarrage
  useEffect(() => {
    const savedGuestFavorites = localStorage.getItem('guestFavorites');
    if (savedGuestFavorites) {
      try {
        setGuestFavorites(JSON.parse(savedGuestFavorites));
      } catch (error) {
        console.error('Erreur lors du chargement des favoris invité:', error);
      }
    }
  }, []);

  const fetchFavorites = useCallback(async () => {
    if (!user) {
      setFavorites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const records = await pb.collection('user_favorites').getFullList({
        filter: `user = "${user.id}"`,
        expand: 'product',
      });
      const userFavorites = records.map(record => ({
        ...(record.expand.product || {}),
        id: record.expand.product?.id,
        favoriteId: record.id,
      }));
      setFavorites(userFavorites.filter(item => item.id));
    } catch (error) {
      console.error("Erreur lors de la récupération des favoris:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const addToFavorites = async (product) => {
    if (!product || !product.id) return;

    if (user) {
      // Utilisateur connecté - utiliser la base de données
      const isAlreadyFavorite = favorites.some(item => item.id === product.id);
      if (isAlreadyFavorite) return;

      try {
        const newRecord = await pb.collection('user_favorites').create({
          user: user.id,
          product: product.id,
        });
        const newFavorite = { ...product, favoriteId: newRecord.id };
        setFavorites(prevFavorites => [...prevFavorites, newFavorite]);
      } catch (error) {
        console.error("Erreur lors de l'ajout aux favoris DB:", error);
      }
    } else {
      // Utilisateur invité - utiliser localStorage
      const isAlreadyFavorite = guestFavorites.some(item => item.id === product.id);
      if (isAlreadyFavorite) return;

      const newFavorite = { ...product };
      const updatedFavorites = [...guestFavorites, newFavorite];
      setGuestFavorites(updatedFavorites);
      localStorage.setItem('guestFavorites', JSON.stringify(updatedFavorites));
    }
  };

  const removeFromFavorites = async (productId) => {
    if (user) {
      // Utilisateur connecté
      const favoriteToRemove = favorites.find(item => item.id === productId);
      if (!favoriteToRemove) return;

      // Optimistic update
      setFavorites(prevFavorites => prevFavorites.filter(item => item.id !== productId));

      try {
        await pb.collection('user_favorites').delete(favoriteToRemove.favoriteId);
      } catch (error) {
        console.error("Erreur lors de la suppression du favori DB:", error);
        // Rollback on error
        setFavorites(favorites);
      }
    } else {
      // Utilisateur invité
      const updatedFavorites = guestFavorites.filter(item => item.id !== productId);
      setGuestFavorites(updatedFavorites);
      localStorage.setItem('guestFavorites', JSON.stringify(updatedFavorites));
    }
  };

  const toggleFavorites = async (product) => {
    if (!product || !product.id) return;

    const currentFavs = user ? favorites : guestFavorites;
    const isAlreadyFavorite = currentFavs.some(item => item.id === product.id);

    if (isAlreadyFavorite) {
      await removeFromFavorites(product.id);
    } else {
      await addToFavorites(product);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  // Obtenir les favoris actuels (connecté ou invité)
  const currentFavorites = useMemo(() => {
    return user ? favorites : guestFavorites;
  }, [user, favorites, guestFavorites]);

  const totalPrice = useMemo(() => {
    return currentFavorites.reduce((total, item) => total + (item.price || 0), 0);
  }, [currentFavorites]);

  const value = {
    favorites: currentFavorites,
    addToFavorites,
    removeFromFavorites,
    toggleFavorites,
    isSidebarOpen,
    toggleSidebar,
    totalPrice,
    loading,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}